import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Add20Regular,
  ArrowDownload20Regular,
  Search20Regular,
  Person24Regular,
  Document24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
  ChevronDown20Regular,
  DocumentAdd20Regular,
  DocumentText20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { ExportarClientesModal } from "../../components/modals/ExportarClientesModal";
import { ContaCorrenteSection } from "./ContaCorrenteSection";
import { RiskBadge } from "../../contasReceber/badges";
import {
  getClientRisk,
  type ClientRisk,
} from "../../contasReceber/contasReceberApi";
import {
  ApiError,
  getClientById,
  getClients,
  type Client as ApiClient,
} from "../../clients/clientsApi";
import { APP_LOCALE } from "../../config/locale";

interface ClienteView {
  id: number;
  nome: string;
  nif: string;
  contactoPrincipal: string;
  emailCliente: string;
  telefoneCliente: string;
  morada?: string;
  codigoPostal?: string;
  localidade?: string;
  pais?: string;
  website?: string;
  notas?: string;
  condicoesPagamento?: string;
  contactoPreferencialNome?: string;
  contactoPreferencialFuncao?: string;
  contactoPreferencialEmail?: string;
  contactoPreferencialTelefone?: string;
  tags: string[];
  tiposObras: string[];
  obrasAtivas: number;
  ultimaInteracao: string;
  logo?: string | null;
}

interface Obra {
  ref: string;
  nome: string;
  estado: string;
  margem: string;
}

interface Orcamento {
  ref: string;
  estado: string;
  valor: string;
  data: string;
}

interface Documento {
  tipo: string;
  ref: string;
  data: string;
}

const formatLastInteraction = (value?: string | null): string => {
  if (!value) return "Sem interações";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Sem interações";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar clientes.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os clientes.";
};

const normalizeWebsiteLink = (
  value?: string | null,
): string | null => {
  if (!value || !value.trim()) return null;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
};

const formatOptional = (value?: string | null): string =>
  value && value.trim() ? value : "—";

const mapClientToView = (client: ApiClient): ClienteView => {
  return {
    id: client.id,
    nome: client.name,
    nif: client.taxId ?? "",
    contactoPrincipal: client.primaryContactName || "—",
    emailCliente: client.email ?? "",
    telefoneCliente: client.phoneNumber ?? "",
    morada: client.address ?? undefined,
    codigoPostal: client.postalCode ?? undefined,
    localidade: client.city ?? undefined,
    pais: client.country ?? undefined,
    website: client.website ?? undefined,
    notas: client.notes ?? undefined,
    condicoesPagamento: client.paymentTerms ?? undefined,
    contactoPreferencialNome:
      client.primaryContactName ?? undefined,
    contactoPreferencialFuncao:
      client.primaryContactRole ?? undefined,
    contactoPreferencialEmail:
      client.primaryContactEmail ?? undefined,
    contactoPreferencialTelefone:
      client.primaryContactPhone ?? undefined,
    tags: client.tags ?? [],
    tiposObras:
      client.workTypes?.map((workType) => workType.name) ?? [],
    obrasAtivas: client.activeProjectsCount ?? 0,
    ultimaInteracao: formatLastInteraction(client.lastInteraction),
    logo: client.logoUrl ?? undefined,
  };
};

export function ClientesView() {
  const navigate = useNavigate();
  const location = useLocation();
  // When navigated from the aging report (Contas a Receber → "ver conta corrente"),
  // pre-select that client and open its conta corrente sub-tab.
  const requestedClientIdRef = useRef<number | null>(
    (location.state as { clientId?: number } | null)?.clientId ?? null,
  );
  const [detailTab, setDetailTab] = useState<"ficha" | "conta">(
    requestedClientIdRef.current != null ? "conta" : "ficha",
  );
  const [clientes, setClientes] = useState<ClienteView[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [selectedClientId, setSelectedClientId] = useState<
    number | null
  >(null);
  // [3.6.3] Risco de crédito do cliente selecionado (chip ao lado de "obras ativas").
  const [clientRisk, setClientRisk] = useState<ClientRisk | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDetailOpen, setIsMobileDetailOpen] =
    useState(false);
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] =
    useState(false);
  const [isExportModalOpen, setIsExportModalOpen] =
    useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadClients = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getClients();
        if (!isMounted) return;

        const mapped = response.map(mapClientToView);
        setClientes(mapped);
        setSelectedClientId((current) => {
          const requested = requestedClientIdRef.current;
          if (requested && mapped.some((item) => item.id === requested)) {
            setIsMobileDetailOpen(true);
            requestedClientIdRef.current = null;
            return requested;
          }
          if (current && mapped.some((item) => item.id === current)) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setClientes([]);
        setSelectedClientId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadClients();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredClientes = useMemo(
    () =>
      clientes.filter((cliente) => {
        const matchesSearch =
          cliente.nome
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          cliente.nif.includes(searchQuery);
        return matchesSearch;
      }),
    [clientes, searchQuery],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredClientes.length === 0) {
      setSelectedClientId(null);
      return;
    }

    const hasSelection = filteredClientes.some(
      (entry) => entry.id === selectedClientId,
    );
    if (!hasSelection) {
      setSelectedClientId(filteredClientes[0].id);
    }
  }, [filteredClientes, isLoading, selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) return;

    let isMounted = true;

    const loadSelectedClient = async () => {
      try {
        const response = await getClientById(selectedClientId);
        if (!isMounted) return;

        const mapped = mapClientToView(response);
        setClientes((current) =>
          current.map((cliente) =>
            cliente.id === selectedClientId ? mapped : cliente,
          ),
        );
      } catch {
        // Keep list data if detail fetch fails.
      }
    };

    loadSelectedClient();

    return () => {
      isMounted = false;
    };
  }, [selectedClientId]);

  // [3.6.3] Carrega o risco de crédito do cliente selecionado (chip + tooltip de razões).
  useEffect(() => {
    if (!selectedClientId) {
      setClientRisk(null);
      return;
    }

    let isMounted = true;
    setClientRisk(null);
    getClientRisk(selectedClientId)
      .then((risk) => {
        if (isMounted) setClientRisk(risk);
      })
      .catch(() => {
        // Sem risco visível se a chamada falhar — não bloqueia a ficha do cliente.
      });

    return () => {
      isMounted = false;
    };
  }, [selectedClientId]);

  const selectedClient = filteredClientes.find(
    (c) => c.id === selectedClientId,
  );

  const obrasCliente: Obra[] = [];
  const orcamentosCliente: Orcamento[] = [];
  const documentosCliente: Documento[] = [];

  const getInitials = (nome: string) => {
    return nome
      .split(" ")
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const getEstadoObraClass = (estado: string) => {
    if (estado === "Em Curso")
      return "bg-chart-1/10 text-chart-1";
    if (estado === "Concluída")
      return "bg-primary/10 text-primary";
    return "bg-muted text-muted-foreground";
  };

  const getEstadoOrcamentoClass = (estado: string) => {
    if (estado === "Enviado")
      return "bg-chart-1/10 text-chart-1";
    if (estado === "Adjudicado")
      return "bg-primary/10 text-primary";
    if (estado === "Não Adjudicado")
      return "bg-muted text-muted-foreground";
    return "bg-chart-2/10 text-chart-2";
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => setIsExportModalOpen(true)}
      >
        <ArrowDownload20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Exportar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/clientes/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Cliente
      </button>
    </>
  );

  const handleClientSelect = (clientId: number) => {
    setSelectedClientId(clientId);
    setIsMobileDetailOpen(true);
    setDetailTab("ficha");
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Clientes"
        subtitle="Lista de clientes e histórico de relação comercial"
        actions={actions}
      />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar clientes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar clientes..."
                : `${filteredClientes.length} cliente${
                    filteredClientes.length !== 1 ? "s" : ""
                  } encontrado${
                    filteredClientes.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar clientes...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredClientes.map((cliente) => (
                <button
                  key={cliente.id}
                  onClick={() => handleClientSelect(cliente.id)}
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedClientId === cliente.id
                      ? "bg-accent/50"
                      : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedClientId === cliente.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedClientId !== cliente.id) {
                      e.currentTarget.style.backgroundColor =
                        "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedClientId !== cliente.id) {
                      e.currentTarget.style.backgroundColor =
                        "var(--card)";
                    }
                  }}
                >
                  {selectedClientId === cliente.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    {cliente.logo ? (
                      <img
                        src={cliente.logo}
                        alt={cliente.nome}
                        className="w-10 h-10 object-cover flex-shrink-0"
                        style={{
                          borderRadius: "var(--radius)",
                        }}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0"
                        style={{
                          fontSize: "var(--text-caption)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          color: "var(--primary)",
                          borderRadius: "var(--radius)",
                        }}
                      >
                        {getInitials(cliente.nome)}
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p
                          className="truncate"
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          {cliente.nome}
                        </p>
                      </div>

                      <p
                        className="text-secondary-text mt-0.5"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Contacto:{" "}
                        {formatOptional(cliente.contactoPrincipal)}
                      </p>

                      <p
                        className="text-secondary-text mt-1"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Última interação: {cliente.ultimaInteracao}
                      </p>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading &&
              !loadError &&
              filteredClientes.length === 0 && (
                <div className="p-8 text-center text-secondary-text">
                  <p>Nenhum cliente encontrado</p>
                </div>
              )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedClient ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <Person24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um cliente para ver os detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {selectedClient.logo ? (
                      <img
                        src={selectedClient.logo}
                        alt={selectedClient.nome}
                        className="w-16 h-16 object-cover flex-shrink-0"
                        style={{
                          borderRadius: "var(--radius)",
                        }}
                      />
                    ) : (
                      <div
                        className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          color: "var(--primary)",
                          borderRadius: "var(--radius)",
                        }}
                      >
                        {getInitials(selectedClient.nome)}
                      </div>
                    )}

                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedClient.nome}
                      </h3>

                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {selectedClient.obrasAtivas > 0 && (
                          <span
                            className="inline-flex px-2 py-1 bg-accent text-accent-foreground rounded"
                            style={{
                              fontSize: "var(--text-caption)",
                              borderRadius: "var(--radius)",
                            }}
                          >
                            {selectedClient.obrasAtivas} obras ativas
                          </span>
                        )}
                        {clientRisk &&
                          clientRisk.clientId === selectedClient.id && (
                            <RiskBadge
                              level={clientRisk.level}
                              title={
                                clientRisk.reasons.length > 0
                                  ? clientRisk.reasons.join("; ")
                                  : undefined
                              }
                            />
                          )}
                      </div>

                      <div className="mt-3 space-y-1">
                        <p
                          className="text-secondary-text"
                          style={{
                            fontSize: "var(--text-caption)",
                          }}
                        >
                          Contacto Principal
                        </p>
                        <p
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          {selectedClient.contactoPrincipal}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{
                        borderRadius: "var(--radius-button)",
                      }}
                      onClick={() =>
                        navigate(`/clientes/editar/${selectedClient.id}`)
                      }
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Cliente</span>
                    </button>

                    <div className="relative">
                      <button
                        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                        style={{
                          borderRadius: "var(--radius-button)",
                        }}
                        onClick={() =>
                          setIsCreateDropdownOpen(
                            !isCreateDropdownOpen,
                          )
                        }
                      >
                        <Add20Regular className="w-5 h-5" />
                        <span>Criar</span>
                        <ChevronDown20Regular className="w-4 h-4" />
                      </button>

                      {isCreateDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() =>
                              setIsCreateDropdownOpen(false)
                            }
                          />

                          <div
                            className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded shadow-lg z-20"
                            style={{
                              borderRadius:
                                "var(--radius-card)",
                            }}
                          >
                            <div className="py-2">
                              <button
                                onClick={() => {
                                  setIsCreateDropdownOpen(
                                    false,
                                  );
                                }}
                                className="w-full px-4 py-2.5 text-left hover:bg-accent transition-colors flex items-center gap-3"
                              >
                                <DocumentAdd20Regular className="w-5 h-5 text-secondary-text" />
                                <span
                                  style={{
                                    fontSize:
                                      "var(--text-base)",
                                  }}
                                >
                                  Nova Encomenda
                                </span>
                              </button>
                              <button
                                onClick={() => {
                                  setIsCreateDropdownOpen(
                                    false,
                                  );
                                }}
                                className="w-full px-4 py-2.5 text-left hover:bg-accent transition-colors flex items-center gap-3"
                              >
                                <DocumentText20Regular className="w-5 h-5 text-secondary-text" />
                                <span
                                  style={{
                                    fontSize:
                                      "var(--text-base)",
                                  }}
                                >
                                  Novo Orçamento
                                </span>
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-1 flex-wrap border-b border-border">
                {(["ficha", "conta"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setDetailTab(tab)}
                    className={`px-4 py-2 border-b-2 transition-colors min-h-[44px] ${
                      detailTab === tab
                        ? "border-primary text-primary"
                        : "border-transparent text-secondary-text hover:text-foreground hover:border-border"
                    }`}
                    style={{
                      marginBottom: "-1px",
                      fontWeight:
                        detailTab === tab
                          ? "var(--font-weight-semi-bold)"
                          : "var(--font-weight-normal)",
                    }}
                  >
                    {tab === "ficha" ? "Ficha" : "Conta Corrente"}
                  </button>
                ))}
              </div>

              {detailTab === "conta" ? (
                <ContaCorrenteSection
                  clientId={selectedClient.id}
                  clientName={selectedClient.nome}
                />
              ) : (
                <>
              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Preferências & Tags
                </h4>

                <div className="space-y-4">
                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Tipo de obras
                    </p>
                    {selectedClient.tiposObras.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedClient.tiposObras.map((tipo) => (
                          <span
                            key={tipo}
                            className="inline-flex px-3 py-1.5 bg-accent text-accent-foreground border border-border rounded"
                            style={{
                              fontSize: "var(--text-caption)",
                              borderRadius: "var(--radius)",
                            }}
                          >
                            {tipo}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-secondary-text">
                        Sem tipos de obra
                      </p>
                    )}
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Tags
                    </p>
                    {selectedClient.tags.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedClient.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex px-3 py-1.5 bg-secondary text-foreground border border-border rounded"
                            style={{
                              fontSize: "var(--text-caption)",
                              borderRadius: "var(--radius)",
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-secondary-text">
                        Sem tags
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Dados do Cliente
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      NIF
                    </p>
                    <p className="mt-0.5">
                      {selectedClient.nif || "—"}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Condições de pagamento
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(
                        selectedClient.condicoesPagamento,
                      )}
                    </p>
                  </div>

                  <div className="sm:col-span-2">
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Notas internas
                    </p>
                    <p className="mt-0.5 whitespace-pre-line">
                      {formatOptional(selectedClient.notas)}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Contacto do Cliente
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Email
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedClient.emailCliente)}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Telefone
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(
                        selectedClient.telefoneCliente,
                      )}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Website
                    </p>
                    {selectedClient.website ? (
                      <a
                        href={
                          normalizeWebsiteLink(
                            selectedClient.website,
                          ) ?? undefined
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 text-primary hover:underline"
                      >
                        {selectedClient.website}
                      </a>
                    ) : (
                      <p className="mt-0.5">—</p>
                    )}
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Morada
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedClient.morada)}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Código postal
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedClient.codigoPostal)}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Localidade
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedClient.localidade)}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      País
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedClient.pais)}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Contacto Preferencial
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Nome
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(
                        selectedClient.contactoPreferencialNome,
                      )}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Função
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(
                        selectedClient.contactoPreferencialFuncao,
                      )}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Email
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(
                        selectedClient.contactoPreferencialEmail,
                      )}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      Telefone
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(
                        selectedClient.contactoPreferencialTelefone,
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Obras do Cliente
                </h4>

                <div className="space-y-3">
                  {obrasCliente.map((obra, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded border border-border hover:border-primary/50 transition-colors"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <div className="flex-1">
                        <p
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          {obra.ref}
                        </p>
                        <p
                          className="text-secondary-text mt-0.5"
                          style={{
                            fontSize: "var(--text-caption)",
                          }}
                        >
                          {obra.nome}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded ${getEstadoObraClass(obra.estado)}`}
                            style={{
                              fontSize: "var(--text-caption)",
                              borderRadius: "var(--radius)",
                            }}
                          >
                            {obra.estado}
                          </span>
                          <span
                            className="text-secondary-text"
                            style={{
                              fontSize: "var(--text-caption)",
                            }}
                          >
                            Margem: {obra.margem}
                          </span>
                        </div>
                      </div>
                      <button
                        className="px-3 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Ver Obra
                      </button>
                    </div>
                  ))}

                  {obrasCliente.length === 0 && (
                    <p className="text-secondary-text text-center py-4">
                      Nenhuma obra registada
                    </p>
                  )}
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Orçamentos do Cliente
                </h4>

                <div className="space-y-3">
                  {orcamentosCliente.map((orc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded border border-border hover:border-primary/50 transition-colors"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <div className="flex-1">
                        <p
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          {orc.ref}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded ${getEstadoOrcamentoClass(orc.estado)}`}
                            style={{
                              fontSize: "var(--text-caption)",
                              borderRadius: "var(--radius)",
                            }}
                          >
                            {orc.estado}
                          </span>
                          <span
                            className="text-secondary-text"
                            style={{
                              fontSize: "var(--text-caption)",
                            }}
                          >
                            {orc.data}
                          </span>
                        </div>
                      </div>
                      <p
                        style={{
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                        }}
                      >
                        {orc.valor}
                      </p>
                    </div>
                  ))}

                  {orcamentosCliente.length === 0 && (
                    <p className="text-secondary-text text-center py-4">
                      Nenhum orçamento registado
                    </p>
                  )}
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Documentos Recentes
                </h4>

                <div className="space-y-2">
                  {documentosCliente.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded border border-border hover:border-primary/50 transition-colors cursor-pointer"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <div className="flex items-center gap-3">
                        <Document24Regular className="w-5 h-5 text-secondary-text" />
                        <div>
                          <p
                            style={{
                              fontWeight:
                                "var(--font-weight-semi-bold)",
                            }}
                          >
                            {doc.tipo}
                          </p>
                          <p
                            className="text-secondary-text"
                            style={{
                              fontSize: "var(--text-caption)",
                            }}
                          >
                            {doc.ref}
                          </p>
                        </div>
                      </div>
                      <p
                        className="text-secondary-text"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        {doc.data}
                      </p>
                    </div>
                  ))}

                  {documentosCliente.length === 0 && (
                    <p className="text-secondary-text text-center py-4">
                      Nenhum documento recente
                    </p>
                  )}
                </div>
              </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ExportarClientesModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        totalClientesFiltrados={filteredClientes.length}
      />
    </div>
  );
}
